import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Send, CheckCircle2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { SocialProfilesList } from "@/components/SocialLinks";

export const Footer = () => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Por favor ingresa un correo electrónico válido");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      setSubscribed(true);
      toast.success("¡Te has suscrito con éxito al boletín!");
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al suscribirte. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="border-t border-white/10 mt-16 px-6 md:px-16 py-12 bg-black/40" data-testid="site-footer">
      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-10 justify-between items-start">
        <div className="flex-1 max-w-md">
          <p className="font-heading text-xl font-bold tracking-tight">
            LA CAJA <span className="prism-text">LGBT</span>
          </p>
          <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{t("footerNote")}</p>

          {/* Redes Sociales Oficiales */}
          <div className="mt-6 pt-6 border-t border-zinc-800/80">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-3 flex items-center gap-1.5">
              <Share2 className="w-3.5 h-3.5 text-[#E11D48]" />
              <span>Síguenos en Redes Sociales</span>
            </h4>
            <SocialProfilesList />
          </div>
        </div>

        {/* Newsletter Box */}
        <div className="w-full lg:w-auto bg-zinc-900/80 border border-zinc-800 p-5 rounded-2xl max-w-md">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-4 h-4 text-[#E11D48]" />
            <h4 className="text-sm font-semibold text-white">Boletín de Cine & Series LGBTQ+</h4>
          </div>
          <p className="text-xs text-zinc-400 mb-4">
            Recibe los estrenos de la semana, recomendaciones exclusivas y artículos de revista en tu correo.
          </p>

          {subscribed ? (
            <div className="flex items-center gap-2 text-emerald-400 text-xs bg-emerald-500/10 border border-emerald-500/20 p-3 rounded-xl">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              <span>¡Suscripción completada! Revisa tu correo de bienvenida.</span>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <input
                type="email"
                placeholder="tu@correo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 bg-black/60 border border-zinc-700/80 rounded-xl px-3 py-2 text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-[#E11D48] transition-colors"
              />
              <button
                type="submit"
                disabled={submitting}
                className="bg-[#E11D48] hover:bg-[#be123c] text-white px-4 py-2 rounded-xl text-xs font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50"
              >
                {submitting ? "Suscrito..." : (
                  <>
                    <span>Suscribirme</span>
                    <Send className="w-3 h-3" />
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto border-t border-zinc-800/80 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-zinc-500">
        <p>© {new Date().getFullYear()} La Caja LGBT. Todos los derechos reservados.</p>
        <div className="flex flex-wrap gap-x-5 gap-y-2 items-center justify-center sm:justify-end">
          <Link to="/legal/terminos" data-testid="footer-terms" className="hover:text-white transition-colors">{t("terms")}</Link>
          <Link to="/legal/privacidad" data-testid="footer-privacy" className="hover:text-white transition-colors">{t("privacy")}</Link>
          <Link to="/legal/cookies" data-testid="footer-cookies" className="hover:text-white transition-colors">{t("cookiesPolicy")}</Link>
          <Link to="/legal/derechos" data-testid="footer-copyright" className="hover:text-white transition-colors">{t("copyrightPage")}</Link>
          <Link to="/legal/formulario-dmca" data-testid="footer-dmca-form" className="text-amber-400 hover:text-amber-300 font-medium transition-colors flex items-center gap-1">
            <span>{t("dmcaForm")}</span>
          </Link>
          <Link to="/legal/descargo" data-testid="footer-disclaimer" className="hover:text-white transition-colors">{t("legalDisclaimer")}</Link>
        </div>
      </div>
    </footer>
  );
};
